import { ExternalLink, Loader2, Pause, Play, Volume1, Volume2, VolumeX, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { get } from "./api";
import { artistNames } from "./format";
import type { TrackLite } from "./types";

type Status = "idle" | "loading" | "playing" | "paused" | "error";

interface PlayerState {
  track: TrackLite | null;
  status: Status;
  progress: number; // 0..1
  link: string | null;
  /** Play this song, or pause/resume it when it is already the current one. */
  toggle(track: TrackLite): void;
  stop(): void;
  volume: number; // 0..1, remembered between visits
  setVolume(v: number): void;
}

const VOLUME_KEY = "poplista.volume";

function savedVolume(): number {
  try {
    const v = Number(localStorage.getItem(VOLUME_KEY));
    return localStorage.getItem(VOLUME_KEY) !== null && v >= 0 && v <= 1 ? v : 0.8;
  } catch {
    return 0.8;
  }
}

const Ctx = createContext<PlayerState | null>(null);

export const usePlayer = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePlayer needs <PlayerProvider>");
  return ctx;
};

/** One shared <audio> element: starting a song stops the previous one. */
export function PlayerProvider({ children }: { children: ReactNode }) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const request = useRef(0); // ignore a slow answer for a song the user has already left
  const [track, setTrack] = useState<TrackLite | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [progress, setProgress] = useState(0);
  const [link, setLink] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(savedVolume);

  useEffect(() => {
    const el = new Audio();
    el.preload = "none";
    el.volume = volume;
    el.addEventListener("timeupdate", () => setProgress(el.duration ? el.currentTime / el.duration : 0));
    el.addEventListener("ended", () => { setStatus("paused"); setProgress(0); });
    el.addEventListener("pause", () => setStatus((s) => (s === "playing" ? "paused" : s)));
    el.addEventListener("playing", () => setStatus("playing"));
    el.addEventListener("error", () => el.src && setStatus("error"));
    audio.current = el;
    return () => { el.pause(); el.removeAttribute("src"); };
  }, []);

  const toggle = useCallback((next: TrackLite) => {
    const el = audio.current;
    if (!el) return;
    if (track?.id === next.id && status !== "error" && el.src) {
      if (el.paused) void el.play().catch(() => setStatus("error"));
      else el.pause();
      return;
    }
    const mine = ++request.current;
    el.pause();
    el.removeAttribute("src");
    setTrack(next);
    setProgress(0);
    setLink(null);
    setStatus("loading");
    get<{ preview: { url: string; link: string | null } | null }>(`/tracks/${next.id}/preview`)
      .then(({ preview }) => {
        if (mine !== request.current) return;
        if (!preview) return setStatus("error");
        setLink(preview.link);
        el.src = preview.url;
        return el.play().catch(() => mine === request.current && setStatus("error"));
      })
      .catch(() => mine === request.current && setStatus("error"));
  }, [track, status]);

  const stop = useCallback(() => {
    request.current++;
    audio.current?.pause();
    audio.current?.removeAttribute("src");
    setTrack(null);
    setStatus("idle");
    setProgress(0);
  }, []);

  const setVolume = useCallback((v: number) => {
    const clamped = Math.min(1, Math.max(0, v));
    if (audio.current) audio.current.volume = clamped;
    setVolumeState(clamped);
    try { localStorage.setItem(VOLUME_KEY, String(clamped)); } catch { /* private mode: just don't remember it */ }
  }, []);

  const value = useMemo(
    () => ({ track, status, progress, link, toggle, stop, volume, setVolume }),
    [track, status, progress, link, toggle, stop, volume, setVolume],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Round play/pause button laid over a cover. */
export function PlayButton({ track }: { track: TrackLite }) {
  const p = usePlayer();
  const mine = p.track?.id === track.id;
  const playing = mine && p.status === "playing";
  const label = playing ? "Zatrzymaj próbkę" : `Odtwórz próbkę: ${track.title}`;
  return (
    <button type="button" className={`play${mine && p.status !== "idle" ? " is-on" : ""}${playing ? " is-playing" : ""}`} aria-label={label} title={label} onClick={() => p.toggle(track)}>
      {mine && p.status === "loading" ? (
        <Loader2 className="spin" size={15} aria-hidden="true" />
      ) : playing ? (
        <Pause size={15} fill="currentColor" aria-hidden="true" />
      ) : (
        <Play size={15} fill="currentColor" aria-hidden="true" />
      )}
    </button>
  );
}

/** Fixed bar at the bottom while a sample is loaded. */
export function PlayerBar() {
  const p = usePlayer();
  if (!p.track) return null;
  const t = p.track;
  const message = p.status === "error" ? "Brak próbki dla tego utworu" : p.status === "loading" ? "Ładowanie…" : "Próbka 30 s";
  return (
    <div className="playerbar" role="region" aria-label="Odtwarzacz">
      <div className="wrap playerbar__in">
        <PlayButton track={t} />
        <div className="playerbar__text">
          <strong>{t.title}</strong>
          <span className="muted">{artistNames(t.artists, t.credit)} · {message}</span>
        </div>
        <VolumeControl />
        {p.link && <a className="playerbar__src" href={p.link} target="_blank" rel="noreferrer noopener">Deezer <ExternalLink size={13} aria-hidden="true" /></a>}
        <button type="button" className="playerbar__close" aria-label="Zamknij odtwarzacz" onClick={p.stop}><X size={18} aria-hidden="true" /></button>
      </div>
      <div className="playerbar__bar" aria-hidden="true"><span style={{ width: `${p.progress * 100}%` }} /></div>
    </div>
  );
}

/** Mute button + slider. Phones hide the slider (the hardware keys control sample volume there). */
function VolumeControl() {
  const { volume, setVolume } = usePlayer();
  const last = useRef(0.8);
  const toggleMute = () => {
    if (volume > 0) { last.current = volume; setVolume(0); } else setVolume(last.current);
  };
  return (
    <div className="volume">
      <button type="button" className="volume__btn" aria-label={volume === 0 ? "Włącz dźwięk" : "Wycisz"} onClick={toggleMute}>
        {volume === 0 ? <VolumeX size={18} aria-hidden="true" /> : volume < 0.5 ? <Volume1 size={18} aria-hidden="true" /> : <Volume2 size={18} aria-hidden="true" />}
      </button>
      <input type="range" min={0} max={1} step={0.05} value={volume} aria-label="Głośność"
        onChange={(e) => setVolume(Number(e.target.value))} />
    </div>
  );
}
